import SHA256 from "crypto-js/sha256";
import posthog from "posthog-js";
import { apiConfig } from "../API/config";
import { getEnv, isProduction, cognitoRegion } from "./env";
import type { ThirdPartyService, UserIdentityParams } from "./types";

let posthogInitialized = false;

// Organizations opted out of PostHog entirely. Stored as SHA256 digests
// because this module ships in the client bundle, where a readable customer
// name would be public.
//
// To opt another org out, add the digest of its LOWER-CASED slug:
//   printf '%s' "<lower-cased-slug>" | shasum -a 256
// The gate lower-cases before hashing, so a digest of any other casing can
// never match and fails silently.
//
// Bailing out of `init` gates ALL PostHog traffic, not just `identify`:
// autocapture, pageview capture and session recording are configured in the
// `posthog.init` options below, so never initializing disables them; the only
// manual capture site (`posthog?.capture(...)` in `useUserAnalytics`) no-ops
// against the uninitialized singleton; and `identify`/`teardown` gate on
// `posthogInitialized`.
const POSTHOG_EXCLUDED_ORGANIZATION_SLUG_HASHES = new Set([
  "dd4efe815a51ab346b6033c5ccac6df1b645ebf7db4fb2e437437b3f3cf7a52c",
]);

const isExcludedOrganization = (organizationSlug?: string): boolean =>
  POSTHOG_EXCLUDED_ORGANIZATION_SLUG_HASHES.has(
    SHA256(organizationSlug?.toLowerCase() ?? "").toString()
  );

// Query-param keys (case-insensitive) that may carry credentials/PII and must
// be stripped from any URL-bearing event property before it leaves the browser.
const POSTHOG_SENSITIVE_QUERY_PARAMS = new Set([
  "token",
  "code",
  "access_token",
  "id_token",
  "refresh_token",
  "email",
  "password",
  "jwt",
  "secret",
  "api_key",
  "apikey",
  "auth",
  "authorization",
  "session",
  "sig",
  "signature",
  "key",
  "otp",
  "seedmessage",
]);

// URL-bearing PostHog event properties. `$pathname` is included because it can
// carry a query string in some capture modes.
const POSTHOG_URL_PROPERTIES = [
  "$current_url",
  "$referrer",
  "$initial_referrer",
  "$pathname",
];

// Strip sensitive query params from a URL/path string, preserving the rest of
// the URL and any non-sensitive params. Handles both absolute URLs and bare
// paths, and guards against non-string / unparseable input so it never throws.
const scrubSensitiveQueryParams = (value: unknown): unknown => {
  if (typeof value !== "string" || value === "") {
    return value;
  }
  try {
    const isAbsolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(value);
    // Synthetic base lets us parse relative paths (e.g. $pathname) too.
    const url = new URL(value, "http://scrub.local");
    let mutated = false;
    [...url.searchParams.keys()].forEach((key) => {
      if (POSTHOG_SENSITIVE_QUERY_PARAMS.has(key.toLowerCase())) {
        url.searchParams.delete(key);
        mutated = true;
      }
    });
    if (!mutated) {
      // Return the original string untouched (avoids URL re-serialization).
      return value;
    }
    return isAbsolute
      ? url.toString()
      : `${url.pathname}${url.search}${url.hash}`;
  } catch {
    // Unparseable value — pass through untouched rather than throwing.
    return value;
  }
};

// PostHog — product analytics. Ingestion goes through our own
// reverse proxy only. Production + real-user only: init/identify/capture/recording
// all no-op outside prod and for impersonation (single project key, so skipping
// init/identify is the only way to send nothing outside prod). PII posture:
// hashed id only, masked recordings, and the $el_text/attr__* autocapture scrub
// in before_send below.
export const PostHogService: ThirdPartyService = {
  initWhenAuthenticated: true,
  init: (params?: UserIdentityParams): void => {
    if (!isProduction || isExcludedOrganization(params?.organization)) {
      return;
    }

    const isRealUser = params?.isRealUser ?? false;

    const posthogKey = getEnv("VITE_POSTHOG_KEY");
    if (!posthogKey) {
      // Fail fast/quiet: never call posthog.init(undefined, ...).
      return;
    }

    posthog.init(posthogKey, {
      api_host: apiConfig.POSTHOG_PROXY_URL, // our reverse proxy, e.g. <api-domain>/ingest
      ui_host: apiConfig.POSTHOG_UI_HOST, // https://us.posthog.com — toolbar/app links only
      persistence: "localStorage",
      // autocapture gated on real users (like pageview + recording below) so
      // impersonation / internal prod sessions auto-capture nothing.
      autocapture: isRealUser,
      mask_all_element_attributes: true, // drop attrs that may hold customer data
      mask_personal_data_properties: true, // PostHog's built-in PII scrub
      // `true` only captures the initial load; SPA route changes need "history_change".
      capture_pageview: isRealUser ? "history_change" : false,
      capture_performance: false,
      debug: false,
      // ---- session recording ----
      disable_session_recording: !isRealUser,
      session_recording: {
        maskAllInputs: true, // mask passwords + every other input value
        maskTextSelector: "*", // mask all rendered text (names/emails/customer data)
      },
      // no sample_rate — record all eligible (real-user) sessions
      //
      // PII gate: autocapture attaches each element's $el_text and attr__* values,
      // which often hold customer data (finding titles, resource/asset names). The
      // mask_* flags don't strip inner text, so redact it here before any
      // $autocapture event leaves the browser.
      before_send: (event) => {
        if (!event) {
          return event;
        }
        if (event.event === "$autocapture") {
          const elements = event.properties?.$elements;
          if (Array.isArray(elements)) {
            (elements as Record<string, unknown>[]).forEach((element) => {
              if (element && typeof element === "object") {
                delete element.$el_text;
                Object.keys(element).forEach((propKey) => {
                  if (propKey.startsWith("attr__")) {
                    delete element[propKey];
                  }
                });
              }
            });
          }
        }
        // For EVERY event, strip sensitive query params (tokens, email, etc.)
        // from URL-bearing properties while keeping normal analytics URLs intact.
        if (event.properties) {
          POSTHOG_URL_PROPERTIES.forEach((prop) => {
            if (prop in event.properties) {
              event.properties[prop] = scrubSensitiveQueryParams(
                event.properties[prop]
              );
            }
          });
        }
        return event;
      },
    });
    posthogInitialized = true;
  },

  identify: ({
    isRealUser,
    hashedUserId,
    organization,
    account,
    role,
    accountType,
    theme,
  }: UserIdentityParams): void => {
    // Production + real-user only, and only once `init` actually ran: `identify`
    // emits a synthetic `$identify` event (plus group / register_once
    // super-props), so gating on `isProduction` alone would leak an identify
    // event for impersonation / non-real prod sessions. And if
    // `VITE_POSTHOG_KEY` is missing, `init` bails, so we must not run these
    // against the uninitialized singleton.
    if (!isProduction || !isRealUser || !posthogInitialized) {
      return;
    }
    // Hashed user id ONLY — never raw userId/email/name.
    posthog.identify(hashedUserId, {
      $name: hashedUserId,
      customer_organization: organization,
      customer_account: account,
      user_id: hashedUserId,
      role,
      account_type: accountType,
    });

    posthog.group("customer_organization", organization, {
      account_type: accountType,
    });

    posthog.register_once({
      customer_organization: organization,
      customer_account: account,
      region: cognitoRegion,
      theme,
    });

    posthog.register({ account_type: accountType });
  },
  teardown: (): void => {
    if (!posthogInitialized) {
      return;
    }
    posthog.reset();
    posthogInitialized = false;
  },
};
